import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
} from 'react';

import { Loader } from '@/components/shared/Loader';
import { hasBootstrapProviderCaches } from '@/helpers/providerCache';
import { isNil } from 'ramda';

import styles from './LoadingProvider.module.scss';

export enum LoadingResource {
	PERMISSIONS = 'permissions',
	SUBSCRIPTION = 'subscription',
	TEAM = 'team',
	TEAM_SONGS = 'team_songs',
	SETLISTS = 'setlists',
}

const buildLoadedResources = (
	loaded: boolean,
): Record<LoadingResource, boolean> => ({
	[LoadingResource.PERMISSIONS]: loaded,
	[LoadingResource.SUBSCRIPTION]: loaded,
	[LoadingResource.TEAM]: loaded,
	[LoadingResource.TEAM_SONGS]: loaded,
	[LoadingResource.SETLISTS]: loaded,
});

export interface LoadingContext {
	isLoading: boolean;
	setLoaded: (resource: LoadingResource) => void;
}

const defaultValue: LoadingContext = {
	isLoading: true,
	setLoaded: () => {},
};

const LoadingContext = createContext<LoadingContext>(defaultValue);

export function useLoading(): LoadingContext {
	const context = useContext(LoadingContext);
	if (isNil(context)) {
		throw new Error(`useSetlists must be used within a SetlistsProvider`);
	}

	return context;
}

export const LoadingProvider: React.FC = ({ children }) => {
	// If everything is already cached we can render straight away; otherwise we
	// wait for each resource provider to report itself as loaded.
	const [loadedResources, setLoadedResources] = React.useState(() =>
		buildLoadedResources(hasBootstrapProviderCaches()),
	);

	const setLoaded = useCallback((resource: LoadingResource) => {
		setLoadedResources((prev) =>
			prev[resource] ? prev : { ...prev, [resource]: true },
		);
	}, []);

	// Derive `isLoading` from the resources themselves so the gate always lifts
	// once every resource has reported in (no separate state to fall out of sync).
	const isLoading = useMemo(
		() => Object.values(loadedResources).some((loaded) => !loaded),
		[loadedResources],
	);

	return (
		<LoadingContext.Provider
			value={{
				isLoading,
				setLoaded,
			}}
		>
			<>
				{isLoading && (
					<div className={styles['root']}>
						<Loader text="resources" />
					</div>
				)}
				{children}
			</>
		</LoadingContext.Provider>
	);
};

export default LoadingProvider;
